import {
    Badge, Box, Flex, Grid, GridItem, Image, Input, Select, Text, Tooltip, IconButton, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, DrawerFooter, FormControl, FormLabel, Button, Textarea, HStack, Modal,
    ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Checkbox,
} from '@chakra-ui/react'
import MyDiv from './branch.style';
import { ChevronDownIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import { AlertCard, CustomToast } from '../../../../components';
import { useState, useEffect } from 'react';
import PlusIcon from "../../../../assets/images/Vector.svg";
import { useFetch } from '../../../../hooks/useFetch';
import { IBranch, ISignatureBlock, IEmployee, ISignatureRow, IBranchGroupDropdown } from './model';
import CustomTables, { Column, ILazyParams } from '../../../../components/CustomTable/CustomTables';

const emptyBlock = (): ISignatureBlock => ({
    searchText: "",
    employees: [],
    selectedEmp: null,
    signatureFile: null,
    isDefault: false,
    forPayslip: false,
    forOnboarding: false,
});

const emptyBranch: IBranch = {
    id: 0, branch_Code: '', branch_Name: '', company_Name: '', address: '', status: true, company_ID: 0,
    meal_Allowance: 0, health_Insurance_Coverage: 0, transportation_Allowance: 0,
    branch_Group_Id: null, branch_Group_Name: '',
};

const Branch = () => {
    const [branches, setBranches] = useState<IBranch[]>([]);
    const [companyDropdown, setCompanyDropdown] = useState<any[]>([]);
    const [branchGroupDropdown, setBranchGroupDropdown] = useState<IBranchGroupDropdown[]>([]);
    const [AlertOpen, setAlertOpen] = useState(false);
    const [DrawerOpen, setDrawerOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentBranch, setCurrentBranch] = useState<IBranch>(emptyBranch);
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [dataLoading, setDataLoading] = useState(false);
    const [statusChangeSource, setStatusChangeSource] = useState<'table' | 'drawer' | null>(null);
    const [drawerMode, setDrawerMode] = useState(false);
    const [signatureBlocks, setSignatureBlocks] = useState<ISignatureBlock[]>([emptyBlock()]);
    const [signatures, setSignatures] = useState<ISignatureRow[]>([]);
    const [employeeId, setEmployeeId] = useState<number | undefined>()
    const [defaultchange, setDefaultchange] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [totalCount, setTotalCount] = useState(0);

    // edit signature state
    const [editOpen, setEditOpen] = useState(false);
    const [editSig, setEditSig] = useState<ISignatureRow | null>(null);
    const [editFile, setEditFile] = useState<File | null>(null);

    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 20,
        sortField: undefined,
        sortOrder: undefined,
        multiSortMeta: [],
        filters: {},
        globalFilter: '',
    });

    useEffect(() => {
        loadCompanyDropdown();
        loadBranchGroupDropdown();
    }, []);

    useEffect(() => {
        loadBranches();
    }, [lazyParams]);

    const loadBranches = async () => {
        setDataLoading(true);
        const params = new URLSearchParams();
        params.set('first', String(lazyParams.first));
        params.set('rows', String(lazyParams.rows));
        if (lazyParams.sortField) params.set('sortField', lazyParams.sortField);
        if (lazyParams.sortOrder) params.set('sortOrder', String(lazyParams.sortOrder));
        if (lazyParams.globalFilter) params.set('globalFilter', lazyParams.globalFilter);
        if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
            params.set('filters', JSON.stringify(lazyParams.filters));
        }

        const response = await fetchApi<any>(`Hrlense_Branch?${params.toString()}`, 'GET');
        if (response) {
            setBranches(response.data ?? []);
            setTotalCount(response.totalRecords ?? 0);
        }
        setDataLoading(false);
    };

    const loadCompanyDropdown = async () => {
        const response = await fetchApi<any[]>('Dropdown/company', 'GET');
        if (response) setCompanyDropdown(response);
    };

    const loadBranchGroupDropdown = async () => {
        const response = await fetchApi<IBranchGroupDropdown[]>('Hrlense_Branch/branch-group/dropdown', 'GET');
        if (response) setBranchGroupDropdown(response);
    };

    const loadSignatures = async (branchId: number) => {
        const res = await fetchApi<ISignatureRow[]>(`Hrlense_Branch/default-signatory/${branchId}`, "GET");
        setSignatures(res || []);
    };

    const handleStatusChange = (id: number, newStatus: boolean) => {
        setSelectedId(id);
        setPendingStatus(newStatus);
        setStatusChangeSource('table');
        setAlertOpen(true);
    };

    const handleDrawerStatusChange = (newStatus: boolean) => {
        setPendingStatus(newStatus);
        setStatusChangeSource('drawer');
        setAlertOpen(true);
    };

    const handleConfirmStatusChange = async () => {
        if (selectedId !== null && pendingStatus !== null) {
            const scoreId = statusChangeSource === "table" ? selectedId : currentBranch.id;

            const response = await fetchApi(`Hrlense_Branch/toggleStatus/${scoreId}`, "PUT", { id: scoreId, status: pendingStatus });
            if (response) {
                if (statusChangeSource === "table") {
                    await loadBranches();
                } else {
                    setCurrentBranch((prev) => ({ ...prev, status: pendingStatus }));
                }
            }
        }
        setAlertOpen(false);
    };

    const handleAddNewBranch = () => {
        setIsEditMode(false);
        setCurrentBranch(emptyBranch);
        setDrawerOpen(true);
    };

    const handleEditBranch = (branch: IBranch) => {
        setIsEditMode(true);
        setSelectedId(branch.id);
        setCurrentBranch(branch);
        setDrawerOpen(true);
    };

    const handleAddMore = () => {
        setSignatureBlocks(prev => [...prev, emptyBlock()]);
    };

    // Debounced employee search for each signature block
    useEffect(() => {
        const timer = setTimeout(() => {
            signatureBlocks.forEach(async (block, index) => {
                if (block.searchText.trim().length < 2) {
                    setSignatureBlocks(prev =>
                        prev.map((b, i) => (i === index && b.employees.length ? { ...b, employees: [] } : b))
                    );
                    return;
                }
                if (block.selectedEmp) return;

                const res = await fetchApi<IEmployee[]>(
                    `Hrlense_Branch/search?branchId=${currentBranch.id}&name=${block.searchText}`,
                    "GET"
                );

                setSignatureBlocks(prev =>
                    prev.map((b, i) => (i === index ? { ...b, employees: res || [] } : b))
                );
            });
        }, 400);
        return () => clearTimeout(timer);
    }, [signatureBlocks.map(b => b.searchText).join("|"), currentBranch.id]);

    const selectEmployee = (index: number, emp: IEmployee) => {
        setSignatureBlocks(prev =>
            prev.map((b, i) =>
                i === index
                    ? { ...b, selectedEmp: { empId: emp.id, empName: emp.name }, searchText: emp.name, employees: [] }
                    : b
            )
        );
    };


    const MAX_SIGNATURE_SIZE = 2 * 1024 * 1024; // 2 MB


    const handleFileChange = (index: number, file: File) => {
        if (file.size > MAX_SIGNATURE_SIZE) {
            addToast({
                message: `File is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 2 MB.`,
                status: "warning",
            });
            return;
        }

        setSignatureBlocks(prev =>
            prev.map((b, i) => (i === index ? { ...b, signatureFile: file } : b))
        );
    };

    const handleUploadSignature = async () => {
        const readyBlocks = signatureBlocks.filter(b => b.selectedEmp && b.signatureFile);

        const oversized = readyBlocks.find(b => b.signatureFile!.size > MAX_SIGNATURE_SIZE);
        if (oversized) {
            addToast({ message: `Signature for ${oversized.selectedEmp!.empName} exceeds the 2 MB limit`, status: "warning", });
            return;
        }

        if (readyBlocks.length === 0) {
            addToast({ message: "Please add at least one employee with signature", status: "warning", });
            return;
        }

        const formData = new FormData();
        formData.append("BranchId", currentBranch.id.toString());

        readyBlocks.forEach((block, index) => {
            formData.append(`Signatories[${index}].EmpId`, block.selectedEmp!.empId.toString());
            formData.append(`Signatories[${index}].EmpName`, block.selectedEmp!.empName);
            formData.append(`Signatories[${index}].Signature`, block.signatureFile!);
            formData.append(`Signatories[${index}].Is_Default`, block.isDefault.toString());
            formData.append(`Signatories[${index}].For_Payslip`, block.forPayslip.toString());
            formData.append(`Signatories[${index}].For_Onboarding`, block.forOnboarding.toString());
        });

        try {
            const res = await fetchApi("Hrlense_Branch/upload-authorized-signatures", "POST", formData, "",
                "",
                true
            );
            if (res?.success) {
                addToast({
                    message: res.message || "Signatures uploaded successfully",
                    status: "success",
                });
                setDrawerMode(false);
                await loadBranches();
            }
        } catch (err: any) {
            addToast({
                message: err?.message || "Signature already exists",
                status: "error",
            });
        }
    };

    const handleSignature = async (branch: IBranch) => {
        setCurrentBranch(branch);
        setDrawerMode(true);
        await loadSignatures(branch.id);
        setSignatureBlocks([emptyBlock()]);
    };

    const handlecheckboxclick = (empID: number) => {
        setEmployeeId(empID)
        setDefaultchange(true)
    }

    const handleDefaultCheckboxChange = async () => {
        if (!employeeId) return;

        await fetchApi(
            `Hrlense_Branch/set-default-signatory?branchId=${currentBranch.id}&empId=${employeeId}`,
            "PUT"
        );

        setSignatures(prev =>
            prev.map(sig => ({
                ...sig,
                isDefault: sig.empId === employeeId
            }))
        );

        addToast({
            message: "Default signature updated",
            status: "success"
        });

        setDefaultchange(false);
    };

    const handleCancelSignature = (index: number) => {
        setSignatureBlocks(prev => {
            if (index === 0) {
                const updated = [...prev];
                updated[0] = emptyBlock();
                return updated;
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleDefaultChange = (index: number) => {
        setSignatureBlocks(prev => {
            const clickedIsDefault = prev[index].isDefault;

            return prev.map((block, i) => ({
                ...block,
                isDefault: i === index ? !clickedIsDefault : false,
            }));
        });
    };

    const handleBlockFlag = (index: number, key: 'forPayslip' | 'forOnboarding') => {
        setSignatureBlocks(prev =>
            prev.map((b, i) => (i === index ? { ...b, [key]: !b[key] } : b))
        );
    };

    // ---------- Edit signature ----------
    const handleEditSignature = (row: ISignatureRow) => {
        setEditSig({ ...row });
        setEditFile(null);
        setEditOpen(true);
    };

    const handleUpdateSignature = async () => {
        if (!editSig) return;

        const formData = new FormData();
        formData.append("BranchId", currentBranch.id.toString());
        formData.append("EmpId", editSig.empId.toString());
        formData.append("Is_Default", editSig.isDefault.toString());
        formData.append("For_Payslip", editSig.forPayslip.toString());
        formData.append("For_Onboarding", editSig.forOnboarding.toString());
        if (editFile) formData.append("Signature", editFile);

        try {
            const res = await fetchApi<any>("Hrlense_Branch/update-authorized-signature", "PUT", formData, "", "", true);
            if (res?.success) {
                addToast({ message: res.message || "Signature updated successfully", status: "success" });
                setEditOpen(false);
                setEditSig(null);
                setEditFile(null);
                await loadSignatures(currentBranch.id);
                await loadBranches(); // keeps the Signature "View" column current
            }
        } catch (err: any) {
            addToast({ message: err?.message || "Failed to update signature", status: "error" });
        }
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;

        if (name === "branch_Name" && !/^[A-Za-z\s]*$/.test(value)) {
            return;
        }

        if (name === "branch_Group_Id") {
            const parsed = value === "" ? null : parseInt(value, 10);
            setCurrentBranch(prev => ({
                ...prev,
                branch_Group_Id: isNaN(parsed as number) ? null : parsed,
            }));
            return;
        }

        if (name === "meal_Allowance" || name === "health_Insurance_Coverage" || name === "transportation_Allowance") {
            if (!/^\d*$/.test(value)) {
                return;
            }

            const parsed = value === "" ? 0 : parseInt(value, 10);
            setCurrentBranch(prev => ({
                ...prev,
                [name]: isNaN(parsed) ? 0 : parsed,
            }));
            return;
        }
        setCurrentBranch(prev => ({ ...prev, [name]: value, }));
    };

    const handleSubmit = async () => {
        if (!currentBranch.company_ID) {
            addToast({ message: "Please select Company Name", status: "warning" });
            return;
        }
        if (!currentBranch.branch_Code.trim()) {
            addToast({ message: "Please enter Branch Code", status: "warning" });
            return;
        }
        if (!currentBranch.branch_Name.trim()) {
            addToast({ message: "Please enter Branch Name", status: "warning" });
            return;
        }
        if (!currentBranch.address.trim()) {
            addToast({ message: "Please enter Address", status: "warning" });
            return;
        }

        let payload = {
            ...currentBranch,
            branch_Group_Id: currentBranch.branch_Group_Id ? Number(currentBranch.branch_Group_Id) : null,
            meal_Allowance: Number(currentBranch.meal_Allowance),
            health_Insurance_Coverage: Number(currentBranch.health_Insurance_Coverage),
            transportation_Allowance: Number(currentBranch.transportation_Allowance)
        }

        let postPayload = {
            branch_Code: currentBranch.branch_Code,
            branch_Name: currentBranch.branch_Name,
            company_Name: currentBranch.company_Name,
            address: currentBranch.address,
            status: currentBranch.status,
            company_Id: currentBranch.company_ID,
            branch_Group_Id: currentBranch.branch_Group_Id ? Number(currentBranch.branch_Group_Id) : null,
            meal_Allowance: Number(currentBranch.meal_Allowance),
            health_Insurance_Coverage: Number(currentBranch.health_Insurance_Coverage),
            transportation_Allowance: Number(currentBranch.transportation_Allowance)
        }

        if (isEditMode) {
            const response = await fetchApi(`Hrlense_Branch/${currentBranch.id}`, 'PUT', payload, null, "Branch updated successfully");
            if (response)
                await loadBranches();
        } else {
            const response = await fetchApi<IBranch>('Hrlense_Branch', 'POST', postPayload, null, "Branch added successfully");

            if (response) await loadBranches();
        }
        setDrawerOpen(false);
    };

    const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");

    const openImage = (url: string) => {
        setPreviewImage(url);
        setImageModalOpen(true);
    };

    return (
        <MyDiv>
            <Box position="relative" minH="300px">
                <Flex justify="space-between" align="center" mb={6}>
                    <Text fontSize="2xl" fontWeight="700">Branch</Text>
                    <Flex gap={2}>
                        <Button leftIcon={<Image src={PlusIcon} boxSize="14px" />} colorScheme="green" size="sm" onClick={handleAddNewBranch}>
                            Add New Branch
                        </Button>
                    </Flex>
                </Flex>

                <Grid className='grid_container'>
                    <GridItem colSpan={12}>
                        <Box position="relative" minH="300px">
                            <CustomTables<IBranch>
                                value={branches}
                                loading={dataLoading}
                                emptyMessage="No Branches Found"
                                preferenceKey="branchTable"
                                showGlobalSearch={false}
                                showToolbar={true}
                                lazy
                                lazyParams={lazyParams}
                                onLazyParamsChange={setLazyParams}
                                totalRecords={totalCount}
                                showPagination
                                rowsPerPageOptions={[20, 100, 200, 500]}
                                maxTableHeight={null}
                            >
                                <Column header="S. No" body={(_, index) => index + 1} />
                                <Column header="Branch Code" field="branch_Code" sortable filterable filterType="text" body={(emp: any) => (
                                    <Badge variant="subtle" colorScheme="green" fontSize="10px">{emp.branch_Code}</Badge>
                                )} />
                                <Column header="Branch Name" field="branch_Name" sortable filterable filterType="text" />
                                <Column header="Branch Group" field="branch_Group_Name" sortable filterable filterType="text" body={(emp: any) => (
                                    emp.branch_Group_Name ? (
                                        <Badge variant="subtle" colorScheme="purple" fontSize="11px">{emp.branch_Group_Name}</Badge>
                                    ) : (
                                        <Text color="gray.400" fontSize="sm">-</Text>
                                    )
                                )} />
                                <Column header="Company Name" field="company_Name" sortable filterable filterType="text" />
                                <Column header="Address" field="address" body={(emp: any) => (
                                    <Tooltip label={emp.address} aria-label="Full address" bg="gray.700" color="white" placement="bottom-start" maxW="300px" hasArrow>
                                        <Box as="span" display="inline-block" maxW="180px" isTruncated fontSize="sm">
                                            {emp.address}
                                        </Box>
                                    </Tooltip>
                                )} />
                                <Column header="Status" field="status" filterable filterType="boolean" body={(emp: any) => (
                                    <Select value={emp.status ? "true" : "false"} onChange={(e) => handleStatusChange(emp.id, e.target.value === "true")} width="120px" height="32px" icon={<ChevronDownIcon />} borderRadius="4px" color={getStatusColor(emp.status)} fontSize="sm" fontWeight="600">
                                        <option value="true" style={{ color: "#38A169" }}>Active</option>
                                        <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                                    </Select>
                                )} />
                                <Column header="Health Insurance Coverage" field='health_Insurance_Coverage' sortable />
                                <Column header="Meal Allowance" field='meal_Allowance' sortable />
                                <Column header="Transportation Allowance" field='transportation_Allowance' sortable />
                                <Column
                                    header="Signature"
                                    body={(row: any) =>
                                        row.signatureImageUrl ? (
                                            <Button size="xs" colorScheme="green" variant="outline" onClick={() => openImage(row.signatureImageUrl)}>
                                                View
                                            </Button>
                                        ) : (
                                            <Text color="gray.400" fontSize="sm">N/A</Text>
                                        )
                                    }
                                />
                                <Column header="Action" frozen="right" body={(emp: any) => (
                                    <Flex align="center" gap={1}>
                                        <Tooltip label="Edit Branch" hasArrow>
                                            <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => handleEditBranch(emp)} />
                                        </Tooltip>
                                        <Tooltip label="Manage Signatures" hasArrow>
                                            <IconButton aria-label="signature" icon={<AddIcon />} size="sm" colorScheme="green" variant="ghost" onClick={() => handleSignature(emp)} />
                                        </Tooltip>
                                    </Flex>
                                )} />
                            </CustomTables>
                        </Box>
                    </GridItem>
                </Grid>

                <AlertCard isOpen={AlertOpen} onClose={() => setAlertOpen(false)} onConfirm={handleConfirmStatusChange} />

                {/* Add / Edit Branch drawer */}
                <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="md">
                    <DrawerOverlay backdropFilter="blur(2px)" />
                    <DrawerContent>
                        <DrawerHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
                            <Text fontWeight="700">{isEditMode ? 'Edit Branch' : 'Add New Branch'}</Text>
                            <Text fontSize="xs" color="gray.400" fontWeight="400" mt={0.5}>
                                {isEditMode ? 'Update branch details below' : 'Fill in the details to create a new branch'}
                            </Text>
                        </DrawerHeader>
                        <DrawerCloseButton />
                        <DrawerBody py={5}>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Company Name</FormLabel>
                                <Select name="company_ID" value={currentBranch.company_ID} onChange={handleInputChange} placeholder="Select Company" bg="gray.50">
                                    {companyDropdown.map((comp) => (
                                        <option key={comp.company_ID} value={comp.company_ID}>
                                            {comp.company_Name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl mb={5}>
                                <FormLabel fontSize="sm" fontWeight="600">Branch Group</FormLabel>
                                <Select name="branch_Group_Id" value={currentBranch.branch_Group_Id ?? ""} onChange={handleInputChange} placeholder="Select Branch Group (Optional)" bg="gray.50">
                                    {branchGroupDropdown.map((grp) => (
                                        <option key={grp.value} value={grp.value}>
                                            {grp.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Branch Code</FormLabel>
                                <Input name="branch_Code" value={currentBranch.branch_Code} onChange={handleInputChange} placeholder="Enter branch Code" bg="gray.50" />
                            </FormControl>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Branch Name</FormLabel>
                                <Input name="branch_Name" value={currentBranch.branch_Name} onChange={handleInputChange} placeholder="Enter branch name" bg="gray.50" />
                            </FormControl>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Address</FormLabel>
                                <Textarea name="address" value={currentBranch.address} onChange={handleInputChange} placeholder="Enter address" bg="gray.50" />
                            </FormControl>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Meal Allowance</FormLabel>
                                <Input name="meal_Allowance" type="text" value={currentBranch.meal_Allowance} onChange={handleInputChange} placeholder="Enter meal allowance" bg="gray.50" />
                            </FormControl>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Health Insurance Coverage</FormLabel>
                                <Input name="health_Insurance_Coverage" type="text" value={currentBranch.health_Insurance_Coverage} onChange={handleInputChange} placeholder="Enter health insurance coverage" bg="gray.50" />
                            </FormControl>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Transportation Allowance</FormLabel>
                                <Input name="transportation_Allowance" type="text" value={currentBranch.transportation_Allowance} onChange={handleInputChange} placeholder="Enter transportation allowance" bg="gray.50" />
                            </FormControl>
                            <FormControl mb={2}>
                                <FormLabel fontSize="sm" fontWeight="600">Status</FormLabel>
                                <Select w="140px" value={currentBranch.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(currentBranch.status)} bg="gray.50">
                                    <option value="true" style={{ color: "#38A169" }}>Active</option>
                                    <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                                </Select>
                            </FormControl>
                        </DrawerBody>
                        <DrawerFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
                            <Button variant="ghost" onClick={() => setDrawerOpen(false)}>Cancel</Button>
                            <Button colorScheme="green" onClick={handleSubmit}>{isEditMode ? "Update Branch" : "Add Branch"}</Button>
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>

                {/* Signature drawer */}
                <Drawer isOpen={drawerMode} placement="right" onClose={() => setDrawerMode(false)} size="lg">
                    <DrawerOverlay backdropFilter="blur(2px)" />
                    <DrawerContent>
                        <DrawerHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
                            <Text fontWeight="700">Manage Signatures</Text>
                            <Text fontSize="xs" color="gray.400" fontWeight="400" mt={0.5}>
                                Search for an employee, upload their signature, and choose where it is used
                            </Text>
                        </DrawerHeader>
                        <DrawerCloseButton />
                        <DrawerBody py={5}>
                            {signatureBlocks.map((block, index) => (
                                <Box key={index} mb={4} p={3} border="1px solid" borderColor="gray.100" borderRadius="md">
                                    <HStack spacing={6} mb={1} wrap="wrap">
                                        <Checkbox isChecked={block.isDefault} onChange={() => handleDefaultChange(index)}>
                                            Set as Default
                                        </Checkbox>
                                        <Checkbox isChecked={block.forPayslip} onChange={() => handleBlockFlag(index, 'forPayslip')}>
                                            For Payslip
                                        </Checkbox>
                                        <Checkbox isChecked={block.forOnboarding} onChange={() => handleBlockFlag(index, 'forOnboarding')}>
                                            For Onboarding Letters
                                        </Checkbox>
                                    </HStack>
                                    <FormControl mb={3} mt={2}>
                                        <FormLabel fontSize="sm" fontWeight="600">Search Employee</FormLabel>
                                        <Input
                                            bg="gray.50"
                                            placeholder="Search employee..."
                                            value={block.searchText}
                                            onChange={(e) => {
                                                const text = e.target.value;
                                                setSignatureBlocks(prev =>
                                                    prev.map((b, i) => (i === index ? { ...b, searchText: text, selectedEmp: null } : b))
                                                );
                                            }}
                                        />
                                    </FormControl>

                                    {block.employees.length > 0 && (
                                        <Box maxH="150px" overflowY="auto" border="1px solid #CBD5E0" borderRadius="md">
                                            {block.employees.map(emp => (
                                                <Box key={emp.id} px={3} py={2} cursor="pointer" _hover={{ bg: "gray.100" }} onClick={() => selectEmployee(index, emp)}>
                                                    {emp.name}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {block.selectedEmp && (
                                        <Box mt={4}>
                                            <FormLabel mb={2} fontSize="sm" fontWeight="600">
                                                Upload Signature — <b>{block.selectedEmp.empName}</b>
                                            </FormLabel>
                                            <Input type="file" accept="image/*" bg="gray.50" p={1} onChange={(e) => e.target.files && handleFileChange(index, e.target.files[0])} />
                                            <Flex mt={3} gap={2} justify="flex-end">
                                                <Button size="sm" variant="ghost" onClick={() => handleCancelSignature(index)}>
                                                    Cancel
                                                </Button>
                                            </Flex>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                            <Flex mt={2} mb={4} gap={2}>
                                <Button size="sm" colorScheme="green" variant="outline" onClick={handleAddMore}>Add More</Button>
                            </Flex>

                            <Text fontSize="lg" fontWeight="700" mt={6} mb={3}>Signature List</Text>
                            {signatures.length > 0 && (
                                <Box mb={6}>
                                    <CustomTables value={signatures} emptyMessage="No Signatures Found" maxTableHeight={null}>
                                        <Column header="S. No" body={(_, index) => index + 1} />
                                        <Column header="Employee Name" body={(row: any) => row.empName} />
                                        <Column header="Signature" body={(row: any) => (
                                            <Image src={row.imageUrl} boxSize="60px" objectFit="contain" border="1px solid #CBD5E0" borderRadius="md" cursor="pointer" onClick={() => openImage(row.imageUrl)} />
                                        )} />
                                        <Column
                                            header="Default"
                                            body={(row: any) => (
                                                <Checkbox isChecked={row.isDefault} onChange={() => handlecheckboxclick(row.empId)} colorScheme="green" />
                                            )}
                                        />
                                        <Column header="Payslip" body={(row: ISignatureRow) => (
                                            <Badge colorScheme={row.forPayslip ? "green" : "gray"}>{row.forPayslip ? "Yes" : "No"}</Badge>
                                        )} />
                                        <Column header="Onboarding" body={(row: ISignatureRow) => (
                                            <Badge colorScheme={row.forOnboarding ? "green" : "gray"}>{row.forOnboarding ? "Yes" : "No"}</Badge>
                                        )} />
                                        <Column header="Action" body={(row: ISignatureRow) => (
                                            <Tooltip label="Edit Signature" hasArrow>
                                                <IconButton aria-label="Edit signature" icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => handleEditSignature(row)} />
                                            </Tooltip>
                                        )} />
                                    </CustomTables>
                                </Box>
                            )}
                            <AlertCard isOpen={defaultchange} onClose={() => setDefaultchange(false)} onConfirm={handleDefaultCheckboxChange}
                                colorScheme="blue"
                                title="Change Default Signature"
                                description="Are you sure you want to change the default signature."
                            />
                        </DrawerBody>
                        <DrawerFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
                            <Button variant="ghost" onClick={() => setDrawerMode(false)}>Cancel</Button>
                            <Button colorScheme="green" onClick={handleUploadSignature}>Submit</Button>
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>

                {/* Edit signature modal */}
                <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} size="md" isCentered>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Edit Signature — {editSig?.empName}</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={4}>
                            {editSig && (
                                <>
                                    <Text fontSize="sm" fontWeight="600" mb={2}>Current Signature</Text>
                                    <Image src={editSig.imageUrl} boxSize="80px" objectFit="contain" border="1px solid #CBD5E0" borderRadius="md" mb={4} />

                                    <FormControl mb={4}>
                                        <FormLabel fontSize="sm" fontWeight="600">Replace Signature (optional)</FormLabel>
                                        <Input type="file" accept="image/*" bg="gray.50" p={1} onChange={(e) => setEditFile(e.target.files?.[0] ?? null)} />
                                    </FormControl>

                                    <Flex direction="column" gap={2}>
                                        <Checkbox isChecked={editSig.isDefault} onChange={() => setEditSig({ ...editSig, isDefault: !editSig.isDefault })}>
                                            Set as Default
                                        </Checkbox>
                                        <Checkbox isChecked={editSig.forPayslip} onChange={() => setEditSig({ ...editSig, forPayslip: !editSig.forPayslip })}>
                                            For Payslip
                                        </Checkbox>
                                        <Checkbox isChecked={editSig.forOnboarding} onChange={() => setEditSig({ ...editSig, forOnboarding: !editSig.forOnboarding })}>
                                            For Onboarding Letters
                                        </Checkbox>
                                    </Flex>
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
                            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
                            <Button colorScheme="green" onClick={handleUpdateSignature}>Update</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Image preview modal */}
                <Modal isOpen={imageModalOpen} onClose={() => setImageModalOpen(false)} size="xl" isCentered>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Signature Preview</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <Image src={previewImage} w="100%" maxH="500px" objectFit="contain" fallback={<Text>No Signature Available</Text>} />
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </Box>
        </MyDiv>
    )
}

export default Branch;